import React, { useCallback, useState } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

import {
    ArrowDropDownOutlined,
    CircleOutlined,
    KeyboardArrowLeftOutlined,
    KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import { Button, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import dayjs from "dayjs";
import { isEmpty } from "lodash";

import CalendarViewYearOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CalendarViewQuarterOutlinedIcon from "@mui/icons-material/DateRangeOutlined";

import CalendarViewDayOutlinedIcon from "@mui/icons-material/CalendarViewDayOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import CalendarViewWeekOutlinedIcon from "@mui/icons-material/CalendarViewWeekOutlined";

const icons = {
    year: CalendarViewYearOutlinedIcon,
    quarter: CalendarViewQuarterOutlinedIcon,
    month: CalendarViewMonthOutlinedIcon,
    isoWeek: CalendarViewWeekOutlinedIcon,
    day: CalendarViewDayOutlinedIcon,
};

const periodTypes = {
    year: {
        label: "Rok",
        icon: CalendarViewYearOutlinedIcon,
    },
    quarter: {
        label: "Čtvrtletí",
        icon: CalendarViewQuarterOutlinedIcon,
    },
    month: {
        label: "Měsíc",
        icon: CalendarViewMonthOutlinedIcon,
    },
    isoWeek: {
        label: "Týden",
        icon: CalendarViewWeekOutlinedIcon,
    },
    day: {
        label: "Den",
        icon: CalendarViewDayOutlinedIcon,
    },
};

//TODO implement functions to handle periods
export const initPeriod = (type, date) => {
    if (isEmpty(type)) {
        type = "month";
    }

    const d = isEmpty(date) ? dayjs() : dayjs(date);
    const start = d.startOf(type).format(FORMAT);
    const end = d.endOf(type).format(FORMAT);

    return {
        type,
        start,
        end,
        date: d.format(FORMAT),
    };
};

const nextPeriod = (period) => {
    let type = (period?.type === "isoWeek" ? "week" : period?.type) ?? "month";
    return initPeriod(period?.type || "month", dayjs(period?.date).add(1, type).format(FORMAT));
};
const previousPeriod = (period) => {
    let type = (period?.type === "isoWeek" ? "week" : period?.type) ?? "month";
    return initPeriod(
        period?.type || "month",
        dayjs(period?.date)
            .add(-1, type || "month")
            .format(FORMAT)
    );
};

export const formatPeriod = (period, dateFormat) => {
    if (isEmpty(period)) {
        return "N/A";
    }

    switch (period.type) {
        case "year":
            return `Rok ${dayjs(period.date).format("YYYY")}`;
        case "quarter":
            return `Q${dayjs(period.date).quarter()}/${dayjs(period.date).format("YYYY")}`;
        case "month":
            return `${dayjs(period.date).format("MMMM YYYY")}`;
        case "isoWeek":
            return `Týden ${dayjs(period.date).isoWeek()}/${dayjs(period.date).format("YYYY")}`;
        case "day":
            return `${dayjs(period.date).format(dateFormat || "DD.MM.YYYY")}`;
        default:
            return `${period.type}: ${dayjs(period.date).format(dateFormat || "DD.MM.YYYY")}`;
    }
};

export const isInPeriod = (date, period) => {
    if (isEmpty(date) || isEmpty(period)) {
        return false;
    }
    const d = dayjs(date);
    const inPeriod = !(dayjs(period.start).isAfter(d) || dayjs(period.end).isBefore(d));
    return inPeriod;
};

const FORMAT = "YYYY-MM-DD";

const PeriodInfo = ({ value, onChange, withDetail, types, disabled }) => {
    let Icon = icons[value.type] || CircleOutlined;
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClose = () => setAnchorEl(null);
    const handleOpen = useCallback(
        (e) => {
            setAnchorEl(e.currentTarget);
        },
        [setAnchorEl]
    );

    const availableTypes = Object.keys(periodTypes).filter(
        (item) => !Array.isArray(types) || types.includes(item)
    );
    const hasMultipleTypes = availableTypes.length > 1;

    return (
        <>
            {hasMultipleTypes && (
                <Button
                    onClick={handleOpen}
                    disabled={disabled}
                    endIcon={<ArrowDropDownOutlined />}
                    disableRipple
                    disableFocusRipple
                    color="inherit">
                    <Icon />
                </Button>
            )}
            {!!withDetail && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}>
                    <Typography variant="body2">{formatPeriod(value)}</Typography>
                    {/*<Typography variant="caption">1.1.2024-31.12.2024</Typography>*/}
                </Box>
            )}
            {hasMultipleTypes && (
                <Menu
                    anchorEl={anchorEl}
                    open={!!anchorEl && !disabled}
                    onClose={handleClose}
                    onClick={handleClose}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: "visible",
                            minWidth: 100,
                            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                            mt: 1.5,
                            "&:before": {
                                content: '""',
                                display: "block",
                                position: "absolute",
                                top: 0,
                                left: 14,
                                width: 10,
                                height: 10,
                                bgcolor: "background.paper",
                                transform: "translateY(-50%) rotate(45deg)",
                                zIndex: 0,
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: "left", vertical: "top" }}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}>
                    {availableTypes.map((item, idx) => {
                        const period = periodTypes[item];
                        const Icon = period.icon || CircleOutlined;
                        return (
                            <MenuItem
                                key={idx}
                                onClick={() => onChange?.(initPeriod(item, value.date))}
                                divider={item.divider}
                                disabled={disabled}>
                                <ListItemIcon>
                                    <Icon />
                                </ListItemIcon>
                                {period.label}
                            </MenuItem>
                        );
                    })}
                </Menu>
            )}
        </>
    );
};

export const PeriodField = ({ value, onChange, withDetail, types, disabled }) => {
    //let Icon = icons[value.type] || CircleOutlined;
    return (
        <>
            <Box
                sx={(theme) => ({
                    display: "flex",
                    flexDirection: "row",
                    gap: theme.spacing(1),
                    alignItems: "center",
                })}>
                <IconButton
                    onClick={() => onChange?.(previousPeriod(value))}
                    sx={{ flex: 0 }}
                    disabled={disabled}>
                    <KeyboardArrowLeftOutlined />
                </IconButton>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        gap: 0,
                        overflowX: "hidden",
                    }}>
                    <PeriodInfo
                        value={value}
                        onChange={onChange}
                        withDetail={true}
                        types={types}
                        disabled={disabled}
                    />
                </Box>
                <IconButton
                    onClick={() => onChange?.(nextPeriod(value))}
                    disabled={disabled}
                    sx={{ flex: 0 }}>
                    <KeyboardArrowRightOutlined />
                </IconButton>
            </Box>
        </>
    );
};

export const PeriodSelector = (props) => {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
                onClick={() => props.onChange?.(previousPeriod(props.value))}
                sx={{ flex: 0 }}
                disabled={props.disabled}>
                <KeyboardArrowLeftOutlined />
            </IconButton>

            <IconButton
                onClick={() => props.onChange?.(nextPeriod(props.value))}
                disabled={props.disabled}
                sx={{ flex: 0 }}>
                <KeyboardArrowRightOutlined />
            </IconButton>
            <PeriodInfo {...props} />
        </Box>
    );
};

/*
Unit	Shorthand	Description
year	y	January 1st, 00:00 this year
quarter	Q	beginning of the current quarter, 1st day of months, 00:00 ( dependent QuarterOfYear plugin )
month	M	the first day of this month, 00:00
week	w	the first day of this week, 00:00 (locale aware)
isoWeek		the first day of this week according to ISO 8601, 00:00 ( dependent IsoWeek plugin )
date	D	00:00 today
day	d	00:00 today
hour	h	now, but with 0 mins, 0 secs, and 0 ms
minute	m	now, but with 0 seconds and 0 milliseconds
second	s	now, but with 0 milliseconds
*/
